import Table, { TableRow } from "../../../../../common/components/molecule/Table/Table"
import { SerialManagementExtended } from "../../../types/meter.types"
import React from "react"
import { StatusCardMiniPresenter } from "../../../../../common/components/atom/StatusCard"
import { CreditMiniPresenter } from "../../../../../common/components/atom/CreditMini"
import { ConsumptionIndexMiniPresenter } from "../../../../../common/components/atom/ConsumptionIndexMini"
import { MinigridNameMiniPresenter } from "../../../../../common/components/atom/MinigridNameMini"
import { InstallationCodeMiniPresenter } from "../../../../../common/components/atom/InstallationCodeMini"
import { LocationNameMiniPresenter } from "../../../../../common/components/atom/LocationNameMini"
import { PoleLinkMiniPresenter } from "../../../../../common/components/atom/PoleLinkMini"
import { GpsMiniPresenter } from "../../../../../common/components/atom/GpsMini"
import RelayStatusMiniPresenter from "../../../../../common/components/atom/RelayStatusMini/RelayStatusMiniPresenter"
import TagCellEditor from "../../../../../common/components/molecule/TagCellEditor/TagCellEditor"
import { PermissionTooltip, useCapability } from "../../../../rbac"
import { useSearchContext, TriStateFilter, ConsumptionSort } from "../SearchContext"
import ColumnSettings, {
  ColumnDefinition,
  ColumnSettingsToolbar,
  loadColumnPreference,
  saveColumnPreference,
} from "../../../../../common/components/molecule/ColumnSettings/ColumnSettings"
import styles from "./MeterV2ListView.module.scss"

export interface MeterV2ListViewProps {
    meterList?: SerialManagementExtended[] | null
    goToSingleMeter: (meterId: string) => void
    isLoading?: boolean
    searchTerm?: string | null
    tagsByProductNumber?: Record<string, string[]>
    onTagSave?: (productNumber: string, nextTags: string[]) => void | Promise<unknown>
    tagSuggestions?: string[]
    locationSuggestions?: string[]
}

type ColumnKey =
  | 'productNumber'
  | 'clientName'
  | 'phone'
  | 'recordTime'
  | 'minigrid'
  | 'installationCode'
  | 'location'
  | 'pole'
  | 'gps'
  | 'consumptionIndex'
  | 'credit'
  | 'relayStatus'
  | 'status'
  | 'tags'

const ALL_COLUMNS: ReadonlyArray<ColumnDefinition<ColumnKey>> = [
  { key: 'productNumber', label: 'Numéro produit' },
  { key: 'clientName', label: 'Nom client' },
  { key: 'phone', label: 'Téléphone' },
  { key: 'recordTime', label: "Date d'enregistrement" },
  { key: 'minigrid', label: 'Minigrid' },
  { key: 'installationCode', label: 'Code installation' },
  { key: 'location', label: 'Localisation' },
  { key: 'pole', label: 'Poteau' },
  { key: 'gps', label: 'GPS' },
  { key: 'consumptionIndex', label: 'Index de consommation' },
  { key: 'credit', label: 'Crédit restant' },
  { key: 'relayStatus', label: 'Statut relais' },
  { key: 'status', label: 'Statut' },
  { key: 'tags', label: 'Tags' },
]

const STORAGE_KEY = 'meterV2List.visibleColumns'
const ALL_COLUMN_KEYS: ReadonlyArray<ColumnKey> = ALL_COLUMNS.map((c) => c.key)
// La colonne du code naît masquée : elle s'active dans les réglages des colonnes.
const DEFAULT_COLUMN_KEYS: ReadonlyArray<ColumnKey> = ALL_COLUMN_KEYS.filter((k) => k !== 'installationCode')

const  MeterV2ListView = ({
    goToSingleMeter,
    meterList,
    isLoading,
    searchTerm,
    tagsByProductNumber,
    onTagSave,
    tagSuggestions,
    locationSuggestions,
}: MeterV2ListViewProps) => {
  const canEditTags = useCapability('equipment.edit')
  const {
    locationQuery,
    setLocationQuery,
    installationCodeQuery,
    setInstallationCodeQuery,
    relayFilter,
    setRelayFilter,
    onlineFilter,
    setOnlineFilter,
    consumptionSort,
    setConsumptionSort,
    consumptionMin,
    setConsumptionMin,
    consumptionMax,
    setConsumptionMax,
    phoneQuery,
    setPhoneQuery,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    creditMin,
    setCreditMin,
    creditMax,
    setCreditMax,
    poleFilter,
    setPoleFilter,
    gpsFilter,
    setGpsFilter,
  } = useSearchContext()

  const [settingsOpen, setSettingsOpen] = React.useState(false)
  const [visibleColumns, setVisibleColumnsState] = React.useState<ColumnKey[]>(() => {
    const cols = loadColumnPreference<ColumnKey>(STORAGE_KEY, ALL_COLUMN_KEYS, DEFAULT_COLUMN_KEYS)
    // Ouverte avec ?installationCode= (lien FieldMate) : la colonne s'affiche,
    // sinon la liste serait filtrée sans que rien ne le montre.
    return installationCodeQuery.trim() !== '' && !cols.includes('installationCode')
      ? ALL_COLUMN_KEYS.filter((k) => k === 'installationCode' || cols.includes(k))
      : cols
  })
  // Masquer la colonne retire son filtre : un filtre invisible fausserait la liste.
  const setVisibleColumns = (next: ColumnKey[]) => {
    if (!next.includes('installationCode') && installationCodeQuery !== '') setInstallationCodeQuery('')
    setVisibleColumnsState(next)
  }

  React.useEffect(() => {
    saveColumnPreference(STORAGE_KEY, visibleColumns)
  }, [visibleColumns])

  const visibleSet = React.useMemo(() => new Set(visibleColumns), [visibleColumns])
  const orderedVisibleColumns = React.useMemo(
    () => ALL_COLUMNS.filter((c) => visibleSet.has(c.key)),
    [visibleSet],
  )

  const onClick = (meterId: string) => {
    if (goToSingleMeter) {
      goToSingleMeter(meterId)
    }
  }

  const meterListFormated = meterList?.reduce<TableRow[]>((acc, meter) => {
    const clientName = meter.clientInfo?.name || '-'
    const clientPhone = meter.clientInfo?.phone || '-'
    const productNumber = meter.product_number || '-'
    const recordTime = meter.record_time ? new Date(meter.record_time).toLocaleDateString() : '-'
    const tags = tagsByProductNumber?.[meter.product_number] ?? []

    const cellsByKey: Record<ColumnKey, string | number | React.ReactNode> = {
      productNumber,
      clientName,
      phone: clientPhone,
      recordTime,
      minigrid: <MinigridNameMiniPresenter key={`minigrid-${meter.id}`} deviceId={meter.product_number} />,
      installationCode: <InstallationCodeMiniPresenter key={`code-${meter.id}`} deviceId={meter.product_number} />,
      location: <LocationNameMiniPresenter key={`location-${meter.id}`} deviceId={meter.product_number} />,
      pole: <PoleLinkMiniPresenter key={`pole-${meter.id}`} deviceId={meter.product_number} />,
      gps: <GpsMiniPresenter key={`gps-${meter.id}`} deviceId={meter.product_number} />,
      consumptionIndex: <ConsumptionIndexMiniPresenter key={`consumption-index-${meter.id}`} deviceId={meter.product_number} />,
      credit: <CreditMiniPresenter key={`credit-${meter.id}`} deviceId={meter.product_number} />,
      relayStatus: <RelayStatusMiniPresenter key={`relay-${meter.id}`} deviceId={meter.product_number} />,
      status: <StatusCardMiniPresenter key={`status-${meter.id}`} deviceId={meter.product_number} />,
      tags: onTagSave ? (
        <PermissionTooltip allowed={canEditTags} key={`tags-${meter.id}`}>
          <TagCellEditor
            tags={tags}
            onSave={(next) => onTagSave(meter.product_number, next)}
            disabled={!canEditTags}
            suggestions={tagSuggestions}
          />
        </PermissionTooltip>
      ) : null,
    }

    acc.push({
      data: orderedVisibleColumns.map((col) => cellsByKey[col.key]),
      id: meter.id,
    })
    return acc
  }, [])

  const emptyMessage = searchTerm && searchTerm.trim() !== ''
    ? `Aucun résultat trouvé pour "${searchTerm}"`
    : "Aucun compteur Jouly trouvé"

  const headerFiltersByKey: Record<ColumnKey, React.ReactNode | null> = {
    productNumber: null,
    clientName: null,
    phone: (
      <input
        key="phone"
        type="text"
        value={phoneQuery}
        onChange={(e) => setPhoneQuery(e.target.value)}
        placeholder="Téléphone…"
        className={styles.input}
      />
    ),
    recordTime: (
      <div key="date" className={styles.consumptionCell}>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={styles.input}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={styles.input}
        />
      </div>
    ),
    minigrid: (() => {
      const suggestions = locationSuggestions ?? []
      const hasCurrent =
        locationQuery === '' || suggestions.includes(locationQuery)
      return (
        <select
          key="loc"
          value={locationQuery}
          onChange={(e) => setLocationQuery(e.target.value)}
          className={styles.select}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="">Tous</option>
          {!hasCurrent && (
            <option value={locationQuery}>{locationQuery}</option>
          )}
          {suggestions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      )
    })(),
    installationCode: (
      <input
        key="installationCode"
        type="text"
        value={installationCodeQuery}
        onChange={(e) => setInstallationCodeQuery(e.target.value)}
        placeholder="MG-BJ-0042…"
        aria-label="Filtrer par code d'installation"
        className={styles.input}
        onClick={(e) => e.stopPropagation()}
      />
    ),
    location: null,
    pole: (
      <select
        key="pole"
        value={poleFilter}
        onChange={(e) => setPoleFilter(e.target.value as TriStateFilter)}
        className={styles.select}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="ALL">Tous</option>
        <option value="ON">Relié</option>
        <option value="OFF">Non relié</option>
      </select>
    ),
    gps: (
      <select
        key="gps"
        value={gpsFilter}
        onChange={(e) => setGpsFilter(e.target.value as TriStateFilter)}
        className={styles.select}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="ALL">Tous</option>
        <option value="ON">Localisé</option>
        <option value="OFF">Non localisé</option>
      </select>
    ),
    consumptionIndex: (
      <div key="consumption" className={styles.consumptionCell}>
        <select
          value={consumptionSort}
          onChange={(e) => setConsumptionSort(e.target.value as ConsumptionSort)}
          className={styles.select}
        >
          <option value="NONE">Tri</option>
          <option value="ASC">Croissant</option>
          <option value="DESC">Décroissant</option>
        </select>
        <div className={styles.minMaxRow}>
          <input
            type="number"
            value={consumptionMin}
            onChange={(e) => setConsumptionMin(e.target.value)}
            placeholder="Min"
            className={styles.numberInput}
          />
          <input
            type="number"
            value={consumptionMax}
            onChange={(e) => setConsumptionMax(e.target.value)}
            placeholder="Max"
            className={styles.numberInput}
          />
        </div>
      </div>
    ),
    credit: (
      <div key="credit" className={styles.minMaxRow}>
        <input
          type="number"
          value={creditMin}
          onChange={(e) => setCreditMin(e.target.value)}
          placeholder="Min"
          className={styles.numberInput}
        />
        <input
          type="number"
          value={creditMax}
          onChange={(e) => setCreditMax(e.target.value)}
          placeholder="Max"
          className={styles.numberInput}
        />
      </div>
    ),
    relayStatus: (
      <select
        key="relay"
        value={relayFilter}
        onChange={(e) => setRelayFilter(e.target.value as TriStateFilter)}
        className={styles.select}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="ALL">Tous</option>
        <option value="ON">ON</option>
        <option value="OFF">OFF</option>
        <option value="NA">NA</option>
      </select>
    ),
    status: (
      <select
        key="online"
        value={onlineFilter}
        onChange={(e) => setOnlineFilter(e.target.value as TriStateFilter)}
        className={styles.select}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="ALL">Tous</option>
        <option value="ON">En ligne</option>
        <option value="OFF">Hors ligne</option>
      </select>
    ),
    tags: null,
  }

  const headerTitle = orderedVisibleColumns.map((c) => c.label)
  const headerFilters = orderedVisibleColumns.map((c) => headerFiltersByKey[c.key])

  return (
    <div className={styles.wrapper}>
      <ColumnSettingsToolbar onClick={() => setSettingsOpen(true)} />
      <Table
        headerTitle={headerTitle}
        headerFilters={headerFilters}
        bodyData={meterListFormated}
        isLoading={isLoading}
        onRowClick={(row) => onClick(row.id)}
        emptyMessage={emptyMessage}
      />
      <ColumnSettings
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        columns={ALL_COLUMNS}
        visibleKeys={visibleColumns}
        onChange={setVisibleColumns}
      />
    </div>
  )
}

export default MeterV2ListView
